<template>
    <div v-if="isSuperAdmin" class="alerts-view">
        <LaPageHeader heading="Alerts"></LaPageHeader>

        <div class="alerts-section">
            <h2 class="section-heading">Alert types</h2>
            <p class="section-hint">
                Alerts of one type are collected for the aggregation window and sent to Slack as one message. Changes
                also apply to alerts already waiting.
                <template v-if="defaultChannel">
                    Types without their own channel post to the default channel
                    <strong data-test="alerts-default-channel">{{ defaultChannel }}</strong
                    >.
                </template>
            </p>
            <LaContentLoader v-if="loadingConfigs && !configs" />
            <div v-else-if="configs" class="table-scroll">
                <LaAlertConfigTable :data="configs" :stats-days="statsDays || 7" :default-channel="defaultChannel" />
            </div>
            <LaNoData v-else header="No alert types" description="Couldn't load the alert types." />
        </div>

        <div class="alerts-section">
            <div class="columns is-vcentered">
                <div class="column">
                    <h2 class="section-heading">Silences</h2>
                    <p class="section-hint">Muted alerts are kept in the log with the status "silenced".</p>
                </div>
                <div class="column is-narrow">
                    <LaBaseButton text="Add silence" icon="plus" data-test="add-silence" @click="addSilence()" />
                </div>
            </div>
            <LaContentLoader v-if="loadingSilences && !silences" />
            <div v-else-if="silences" class="table-scroll">
                <LaAlertSilenceTable :data="silences" />
            </div>
        </div>

        <div class="alerts-section">
            <div class="columns is-vcentered is-multiline">
                <div class="column">
                    <h2 class="section-heading">Recent alerts</h2>
                </div>
                <div class="column is-narrow filter">
                    <LaBaseSelect
                        data-test="alert-log-code"
                        :items="codeFilterItems"
                        v-model="codeFilter"
                        :searchable="false"
                        :stretch="true"
                    />
                </div>
                <div class="column is-narrow filter">
                    <LaBaseSelect
                        data-test="alert-log-status"
                        :items="statusFilterItems"
                        v-model="statusFilter"
                        :searchable="false"
                        :stretch="true"
                    />
                </div>
                <div class="column is-narrow">
                    <LaBaseButton
                        text="Refresh"
                        icon="refresh"
                        buttonType="info"
                        data-test="refresh-alerts"
                        @click="loadAlerts()"
                    />
                </div>
            </div>
            <LaContentLoader v-if="loadingAlerts && !alerts" />
            <div v-else-if="alerts" class="table-scroll">
                <LaAlertLogTable :data="alerts" />
            </div>
        </div>

        <LaDialog name="alertConfigForm" size="large" persistent>
            <LaAlertConfigForm />
        </LaDialog>

        <LaDialog name="alertTest" size="large">
            <LaAlertTestDialog />
        </LaDialog>

        <LaDialog name="alertSilenceForm" persistent>
            <LaAlertSilenceForm />
        </LaDialog>

        <LaDialog name="silenceDelete">
            <LaDeleteConfirmDialog item-name="silence" item-property="reason" />
        </LaDialog>
    </div>
</template>

<script>
import LaPageHeader from "@/components/PageHeader";
import LaContentLoader from "@/components/loaders/ContentLoader";
import LaNoData from "@/components/NoData";
import LaDialog from "@/components/dialog/Dialog";
import LaBaseButton from "@/components/button/BaseButton";
import LaBaseSelect from "@/components/inputs/BaseSelect";
import LaDeleteConfirmDialog from "@/components/dialog/DeleteConfirmDialog";
import LaAlertConfigForm from "@/components/dialog/AlertConfigForm";
import LaAlertSilenceForm from "@/components/dialog/AlertSilenceForm";
import LaAlertTestDialog from "@/components/dialog/AlertTestDialog";
import LaAlertConfigTable from "@/components/alerts/AlertConfigTable";
import LaAlertSilenceTable from "@/components/alerts/AlertSilenceTable";
import LaAlertLogTable from "@/components/alerts/AlertLogTable";
import { ALERT_STATUSES } from "@/components/alerts/helpers";
import store from "@/store/store";
import { computed, ref, watch } from "vue";
import { mutationTypes as dialogMutationTypes } from "@/store/Dialog/types";
import { getterTypes as loggedUserGetterTypes } from "@/store/LoggedUser/types";
import { actionTypes as alertActionTypes, getterTypes as alertGetterTypes } from "@/store/Alerts/types";

const ALL_TYPES = "all types";
const ALL_STATUSES = "all statuses";

export default {
    name: "LaAlertsView",
    components: {
        LaPageHeader,
        LaContentLoader,
        LaNoData,
        LaDialog,
        LaBaseButton,
        LaBaseSelect,
        LaDeleteConfirmDialog,
        LaAlertConfigForm,
        LaAlertSilenceForm,
        LaAlertTestDialog,
        LaAlertConfigTable,
        LaAlertSilenceTable,
        LaAlertLogTable,
    },
    setup() {
        const userRights = computed(() => store.getters[loggedUserGetterTypes.getUserRights]);
        const isSuperAdmin = computed(() => userRights.value && userRights.value["super_admin"]);

        const configs = computed(() => store.getters[alertGetterTypes.getConfigs]);
        const statsDays = computed(() => store.getters[alertGetterTypes.getStatsDays]);
        const defaultChannel = computed(() => store.getters[alertGetterTypes.getDefaultChannel]);
        const silences = computed(() => store.getters[alertGetterTypes.getSilences]);
        const alerts = computed(() => store.getters[alertGetterTypes.getAlerts]);
        const loadingConfigs = computed(() => store.getters[alertGetterTypes.isLoadingConfigs]);
        const loadingSilences = computed(() => store.getters[alertGetterTypes.isLoadingSilences]);
        const loadingAlerts = computed(() => store.getters[alertGetterTypes.isLoadingAlerts]);

        const codeFilter = ref(ALL_TYPES);
        const statusFilter = ref(ALL_STATUSES);
        const codeFilterItems = computed(() => [ALL_TYPES, ...(configs.value || []).map((config) => config.code)]);
        const statusFilterItems = [ALL_STATUSES, ...ALERT_STATUSES];

        const loadAlerts = () => {
            store.dispatch(alertActionTypes.getAlerts, {
                code: codeFilter.value === ALL_TYPES ? null : codeFilter.value,
                status: statusFilter.value === ALL_STATUSES ? null : statusFilter.value,
            });
        };
        watch([codeFilter, statusFilter], loadAlerts);

        const addSilence = () => {
            store.commit(dialogMutationTypes.openDialog, {
                dialogName: "alertSilenceForm",
                dialogHeading: "Add silence",
            });
        };

        if (isSuperAdmin.value) {
            store.dispatch(alertActionTypes.getConfigs);
            store.dispatch(alertActionTypes.getSilences);
            loadAlerts();
        }

        return {
            isSuperAdmin,
            configs,
            statsDays,
            defaultChannel,
            silences,
            alerts,
            loadingConfigs,
            loadingSilences,
            loadingAlerts,
            codeFilter,
            statusFilter,
            codeFilterItems,
            statusFilterItems,
            loadAlerts,
            addSilence,
        };
    },
};
</script>

<style lang="scss" scoped>
// not <section>: the login styles make every section a full-height background panel
.alerts-section {
    margin-bottom: 2.5rem;
}
.section-heading {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 0.3rem;
}
.section-hint {
    font-size: 0.85rem;
    opacity: 0.7;
    margin-bottom: 0.8rem;
}
.filter {
    min-width: 14rem;
}
// Seven columns do not fit a phone - scroll the table instead of squeezing the cells
.table-scroll {
    overflow-x: auto;
    > * {
        min-width: 56rem;
    }
}
</style>
